import React, { useEffect, useRef, useState } from "react";

// Neutral local placeholder (no external dependency) shown after all retries fail.
export const IMAGE_PLACEHOLDER =
  "data:image/svg+xml;charset=utf-8," +
  encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300">
       <rect width="400" height="300" fill="#0f172a"/>
       <g fill="#334155">
         <path d="M120 190l45-55 35 42 28-32 52 65H120z"/>
         <circle cx="150" cy="120" r="18"/>
       </g>
       <text x="200" y="250" fill="#64748b" font-family="sans-serif" font-size="16" text-anchor="middle">Image indisponible</text>
     </svg>`
  );

const RETRY_DELAYS = [500, 1000, 2000]; // 3 retries with backoff

/**
 * <img> with automatic retry on transient load failure (e.g. CDN 500 cold-miss).
 * - Retries up to 3 times (500ms / 1s / 2s).
 * - Retries 1-2 re-request the same URL (edge warms up); the LAST retry appends a
 *   cache-buster (?r=) to bypass a poisoned edge — done late to avoid a thundering
 *   herd on the origin during traffic spikes.
 * - After all retries, shows a clean local placeholder (never the broken-image icon).
 */
export const SmartImage = ({ src, alt = "", className, testId, ...rest }) => {
  const [attempt, setAttempt] = useState(0);
  const [failed, setFailed] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    setAttempt(0);
    setFailed(false);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [src]);

  if (!src || failed) {
    return (
      <img
        src={IMAGE_PLACEHOLDER}
        alt={alt}
        className={className}
        data-testid={testId ? `${testId}-placeholder` : undefined}
        {...rest}
      />
    );
  }

  // Compute the effective src for the current attempt.
  let effectiveSrc = src;
  if (attempt >= RETRY_DELAYS.length && src) {
    const sep = src.includes("?") ? "&" : "?";
    effectiveSrc = `${src}${sep}r=${attempt}`;
  }

  const handleError = () => {
    if (attempt < RETRY_DELAYS.length) {
      const delay = RETRY_DELAYS[attempt];
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setAttempt((a) => a + 1), delay);
    } else {
      setFailed(true);
    }
  };

  return (
    <img
      key={attempt}
      src={effectiveSrc}
      alt={alt}
      className={className}
      data-testid={testId}
      onError={handleError}
      {...rest}
    />
  );
};

export default SmartImage;
